/**
 * CitiesContext - 全局城市数据 Provider
 * 单一职责：加载一次城市数据，全局共享
 * 替代各组件从 constants.ts 导入 CITIES
 */
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { getCities, type CityFromAPI } from '../api/cities';

interface CitiesContextValue {
  /** 所有城市列表 */
  cities: CityFromAPI[];
  /** 按 ID 快速查找 */
  citiesMap: Map<string, CityFromAPI>;
  /** 城市职位数映射 { cityId: jobCount } */
  cityJobCounts: Record<string, number>;
  /** 是否正在加载 */
  loading: boolean;
  /** 加载错误 */
  error: string | null;
  /** 手动刷新 */
  refresh: () => Promise<void>;
}

const CitiesContext = createContext<CitiesContextValue | null>(null);

export function CitiesProvider({ children }: { children: React.ReactNode }) {
  const [cities, setCities] = useState<CityFromAPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCities = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCities();
      setCities(data);
    } catch (err) {
      console.error('Failed to fetch cities:', err);
      setError('Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  const citiesMap = useMemo(() => {
    const map = new Map<string, CityFromAPI>();
    cities.forEach(city => map.set(city.id, city));
    return map;
  }, [cities]);

  const cityJobCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    cities.forEach(city => {
      counts[city.id] = city.jobCount;
    });
    return counts;
  }, [cities]);

  const value = useMemo<CitiesContextValue>(() => ({
    cities,
    citiesMap,
    cityJobCounts,
    loading,
    error,
    refresh: fetchCities,
  }), [cities, citiesMap, cityJobCounts, loading, error]);

  return (
    <CitiesContext.Provider value={value}>
      {children}
    </CitiesContext.Provider>
  );
}

/**
 * 使用城市数据的 Hook
 */
export function useCities(): CitiesContextValue {
  const ctx = useContext(CitiesContext);
  if (!ctx) {
    throw new Error('useCities must be used within a CitiesProvider');
  }
  return ctx;
}
